<template>
	<v-select ref="selectRef" v-bind="$attrs" :label="props.label" :items="sortedItems" :item-title="!props.titleKeyTranslate ? props.titleKey : (item) => t(item[props.titleKey])"
		:item-value="props.itemValue" v-model="selectItems" :menu-props="menuProps"
		 :clearable="resolvedClearable" :multiple="props.multiple" :readonly="fieldReadonly"
		 :rules="validationRules" :density="props.density" variant="outlined" hide-details="auto" @update:menu="isMenuOpen = $event" @click="onFieldClick">
		<template v-if="sortedItems.length > 4" #menu-header="{ search, filteredItems: filtered }">
			<div class="pa-2">
				<v-text-field v-model="search.value" :error="!!search.value && !filtered.length" :label="t('Search')" density="compact" variant="outlined" prepend-inner-icon="search" clearable hide-details></v-text-field>
			</div>
			<v-list-item @click="toggle" v-if="props.multiple">
				<template #prepend>
					<v-checkbox-btn :indeterminate="selectlikesSomeItems && !likesAllItems" :model-value="likesAllItems"></v-checkbox-btn>
					<span class="ml-2">{{ likesAllItems ? t('DeselectAll') : t('SelectAll') }}</span>
				</template>
			</v-list-item>
			<v-divider v-if="props.multiple"></v-divider>
		</template>
		<template v-if="props.multiple" v-slot:selection="{ item, index }">
			<v-chip v-if="index < props.maxDisplayedChips" :text="props.titleKeyTranslate ? t(item[props.titleKey]) : item[props.titleKey]"></v-chip>
			<span v-if="index === props.maxDisplayedChips" class="select-control__overflow-count">(+{{ selectItems.length - props.maxDisplayedChips }})</span>
		</template>
	</v-select>

	<v-bottom-sheet v-model="mobileSheetOpen" :attach="overlayTarget || undefined" class="select-control__sheet-overlay">
		<v-card class="select-control__sheet">
			<v-card-title class="select-control__sheet-title">
				{{ props.label }}
				<v-spacer></v-spacer>
				<v-btn icon="close" variant="tonal" color="primary" density="comfortable" @click="mobileSheetOpen = false" />
			</v-card-title>

			<div v-if="sortedItems.length > 4" class="pa-2">
				<v-text-field v-model="sheetSearch" :label="t('Search')" density="compact" variant="outlined" prepend-inner-icon="search" clearable hide-details></v-text-field>
			</div>

			<template v-if="props.multiple">
				<v-list-item @click="toggle">
					<template #prepend>
						<v-checkbox-btn :indeterminate="selectlikesSomeItems && !likesAllItems" :model-value="likesAllItems"></v-checkbox-btn>
						<span class="ml-2">{{ likesAllItems ? t('DeselectAll') : t('SelectAll') }}</span>
					</template>
				</v-list-item>
				<v-divider></v-divider>
			</template>

			<v-list class="select-control__sheet-list" density="comfortable">
				<v-list-item v-for="item in sheetFilteredItems" :key="item[props.itemValue]" :active="!props.multiple && isItemSelected(item)" color="primary" @click="onSheetItemClick(item)">
					<template v-if="props.multiple" #prepend>
						<v-checkbox-btn :model-value="isItemSelected(item)" @click.stop="onSheetItemClick(item)"></v-checkbox-btn>
					</template>
					<v-list-item-title>{{ itemTitle(item) }}</v-list-item-title>
				</v-list-item>
			</v-list>
		</v-card>
	</v-bottom-sheet>
</template>

<script setup>
// #region Imports

import { shallowRef, inject, computed, ref, watch, watchEffect, onMounted, onUnmounted } from 'vue';
import { useTranslator } from '@/composables/useTranslator';
import { overlayHandler } from '@/utils/ui/overlayHandler';
import { deviceUtils } from '@/utils/core/deviceUtils';
import { useOverlayScrimClose } from '@/composables/useOverlayScrimClose';

// #endregion

// #region Refs & Vars

const props = defineProps({
	label: { type: String, default: '' },
	items: { type: Array, default: () => [] },
	titleKey: { type: String, default: 'name' },
	titleKeyTranslate: { type: Boolean, default: false },
	multiple: { type: Boolean, default: false },
	required: { type: Boolean, default: false },
	itemValue: { type: String, default: 'id' },
	clearable: { type: Boolean, default: null },
	// Keeps the select's look but blocks its own menu, for callers that open their own picker on click.
	readonly: { type: Boolean, default: false },
	sortItems: { type: Boolean, default: true },
	modelValue: { type: [Array, String, Number, null], default: undefined },
	preSelected: { type: [Array, String, Number, null], default: null },
	density: { type: String, default: 'compact' },
	maxDisplayedChips: { type: Number, default: 2 },
});
const emit = defineEmits(['update:modelValue', 'change']);

const t = useTranslator();
const overlayTarget = inject('overlayTarget', null);
const isMenuOpen = ref(false);
const selectRef = ref(null);
const selectItems = shallowRef(props.multiple ? [] : null);
const menuProps = computed(() => {
	return {
		attach: overlayTarget || undefined,
		scrim: true,
		scrollStrategy: 'close',
	};
});
const validationRules = computed(() => {
	if (!props.required) return [];
	return [
		(v) => {
			if (props.multiple) return (Array.isArray(v) && v.length > 0) || t('FieldRequired');
			return (v !== null && v !== undefined && v !== '') || t('FieldRequired');
		},
	];
});
const sortedItems = computed(() => {
	if (!props.sortItems) {
		return [...props.items];
	}

	return [...props.items].sort((a, b) => {
		const titleA = props.titleKeyTranslate ? t(a[props.titleKey]) : a[props.titleKey];
		const titleB = props.titleKeyTranslate ? t(b[props.titleKey]) : b[props.titleKey];
		return String(titleA).localeCompare(String(titleB));
	});
});
const resolvedClearable = computed(() => (typeof props.clearable === 'boolean' ? props.clearable : !props.required));
const filterIsDirty = computed(() => {
	if (props.multiple) return selectItems.value.length > 0 && selectItems.value.length !== props.items.length;
	return selectItems.value !== null && selectItems.value !== undefined;
});

// On mobile the field's own v-select menu is replaced by a v-bottom-sheet (à la UserViewControl.vue): the
// field itself goes readonly (its native menu never opens) and a click on it opens the sheet instead. A
// caller-supplied `readonly` (e.g. a field that opens its own custom picker) always wins and disables this.
const isTablet = ref(deviceUtils.isTablet());
function onResize() {
	isTablet.value = deviceUtils.isTablet();
}
onMounted(() => window.addEventListener('resize', onResize));
onUnmounted(() => window.removeEventListener('resize', onResize));

const mobileSheetEnabled = computed(() => isTablet.value && !props.readonly);
const fieldReadonly = computed(() => props.readonly || mobileSheetEnabled.value);
const mobileSheetOpen = ref(false);
const sheetSearch = ref('');
const sheetFilteredItems = computed(() => {
	if (!sheetSearch.value) return sortedItems.value;
	const query = sheetSearch.value.toLowerCase();
	return sortedItems.value.filter((item) => String(itemTitle(item)).toLowerCase().includes(query));
});

useOverlayScrimClose(
	mobileSheetOpen,
	() => {
		mobileSheetOpen.value = false;
	},
	overlayTarget,
	'.select-control__sheet-overlay'
);

watch(isMenuOpen, () => {
	overlayHandler(selectRef);
});

watch(
	() => props.preSelected,
	(val) => {
		if (props.modelValue !== undefined) {
			return;
		}

		selectItems.value = props.multiple ? (Array.isArray(val) ? val : []) : val;
	}
);

watch(
	() => props.modelValue,
	(val) => {
		if (val === undefined) {
			return;
		}

		selectItems.value = props.multiple ? (Array.isArray(val) ? val : []) : val;
	},
	{ immediate: true }
);

// #endregion

// #region Filters Data

if (props.modelValue === undefined) {
	selectItems.value = props.multiple ? (Array.isArray(props.preSelected) ? props.preSelected : []) : props.preSelected;
}

watch(
	() => selectItems.value,
	(val) => {
		emit('update:modelValue', val);
		emit('change', val);
	},
	{ deep: true }
);

const likesAllItems = computed(() => {
	return selectItems.value.length === props.items.length;
});

const selectlikesSomeItems = computed(() => {
	return selectItems.value.length > 0;
});

// #endregion

// #region Methods

function toggle() {
	if (selectItems.value.length === sortedItems.value.length) {
		selectItems.value = [];
	} else {
		selectItems.value = sortedItems.value.map((t) => t[props.itemValue]);
	}
}

function onFieldClick() {
	if (!mobileSheetEnabled.value) return;
	sheetSearch.value = '';
	mobileSheetOpen.value = true;
}

function itemTitle(item) {
	return props.titleKeyTranslate ? t(item[props.titleKey]) : item[props.titleKey];
}

function isItemSelected(item) {
	const value = item[props.itemValue];
	return props.multiple ? selectItems.value.includes(value) : selectItems.value === value;
}

function onSheetItemClick(item) {
	const value = item[props.itemValue];
	if (props.multiple) {
		const index = selectItems.value.indexOf(value);
		selectItems.value = index > -1 ? selectItems.value.filter((v) => v !== value) : [...selectItems.value, value];
		return;
	}

	selectItems.value = value;
	mobileSheetOpen.value = false;
}

defineExpose({
	getValues() {
		if (props.multiple) return selectItems.value.length > 0 && selectItems.value.length != props.items.length ? selectItems.value : null;
		return selectItems.value ?? null;
	},
	getFullValues() {
		if (props.multiple) return props.items.filter((item) => selectItems.value.includes(item[props.itemValue]));
		return props.items.find((item) => item[props.itemValue] === selectItems.value) ?? null;
	},
	isDirty() {
		return filterIsDirty.value;
	},
	isValid() {
		if (!props.required) return true;
		if (props.multiple) return Array.isArray(selectItems.value) && selectItems.value.length > 0;
		return selectItems.value !== null && selectItems.value !== undefined && selectItems.value !== '';
	},
	isRequired() {
		return props.required;
	},
	selectItems(items) {
		selectItems.value = items;
	},
	reset() {
		selectItems.value = props.multiple ? [] : null;
	},
});

// #endregion
</script>

<style scoped lang="scss">
:deep(.v-field__input) {
	flex-wrap: nowrap;
	min-width: 0;
}

:deep(.v-select__selection) {
	min-width: 0;
	flex-shrink: 1;
	overflow: hidden;
}

:deep(.v-select__selection:has(.select-control__overflow-count)) {
	flex-shrink: 0;
}

:deep(.v-chip__content) {
	overflow: hidden;
	text-overflow: ellipsis;
}

.select-control__sheet {
	display: flex;
	flex-direction: column;
	max-height: 85vh;
}

.select-control__sheet-title {
	display: flex;
	align-items: center;
	gap: 8px;
}

.select-control__sheet-list {
	overflow-y: auto;
}
</style>
